import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import type {ProductSelectorSectionSliceDefaultPrimaryProductsItem} from 'prismicio-types'
import {useEffect, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {Icon} from 'src/common/Icon'
import {UnderlinedTooltip} from './UnderlinedTooltip'

export const RadioButtonField = ({
  product,
  enableReskin = false,
}: {
  product: ProductSelectorSectionSliceDefaultPrimaryProductsItem
  enableReskin?: boolean
}) => {
  const {register, watch} = useFormContext()
  const initialState = watch('product') === product.parameter_value
  const [isSelected, setIsSelected] = useState(initialState)

  // Subscribe to the product field
  useEffect(() => {
    const {unsubscribe} = watch((data) => {
      if (data.product === product.parameter_value) {
        setIsSelected(true)
      } else if (isSelected) {
        setIsSelected(false)
      }
    })

    return () => unsubscribe()
  }, [watch, product.parameter_value, isSelected])

  return (
    <label
      className={cn('flex w-full flex-col gap-2.5 rounded-lg bg-moss-white p-3 ring-[1px] md:p-4', {
        'ring-grey-120 hover:ring-grey-200 hover:shadow-elevation-1': !isSelected,
        'ring-green-300': isSelected && !enableReskin,
        'ring-green-default': isSelected && enableReskin,
      })}
    >
      <div className="flex items-center gap-3">
        <input type="radio" value={product.parameter_value || ''} {...register('product')} className="peer sr-only" />
        <span className="flex size-5 items-center justify-center rounded-full border-[1px] border-grey-200 peer-checked:bg-grey-700">
          <span className="block size-2 rounded-full bg-moss-white" />
        </span>
        <span
          className={cn('text-body-bold text-moss-black', {
            'text-body-bold': !enableReskin,
            'text-tg-body-l': enableReskin,
          })}
        >
          {product.product_name}
        </span>
      </div>
      <div className="flex flex-col pl-8">
        <PrismicRichText
          field={product.locked_features}
          components={{
            list: ({children}) => <ul className="pb-1">{children}</ul>,
            listItem: ({children}) => (
              <li
                className={cn('flex items-start gap-1.5 text-moss-black', {
                  'text-body-m': !enableReskin,
                  'text-tg-body-m': enableReskin,
                })}
              >
                <Icon
                  name="lock"
                  className={cn('mt-0.5 size-4 text-grey-900', {
                    'text-grey-900': !enableReskin,
                    'moss-grey-500': enableReskin,
                  })}
                />
                {children}
              </li>
            ),
          }}
        />
        <PrismicRichText
          field={product.features}
          components={{
            list: ({children}) => <ul className="flex flex-col gap-1 pb-1">{children}</ul>,
            listItem: ({children}) => (
              <li
                className={cn('flex items-start gap-1.5 text-moss-black', {
                  'text-body-m': !enableReskin,
                  'text-tg-body-m': enableReskin,
                })}
              >
                <Icon
                  name="filled-check-v2"
                  className={cn('mt-0.5 size-4 min-w-4', {
                    'text-green-300': !enableReskin,
                    'text-green-default': enableReskin,
                  })}
                />
                {children}
              </li>
            ),
            paragraph: ({children}) => (
              <p
                className={cn('pt-1 text-moss-black', {
                  'text-body-m': !enableReskin,
                  'text-tg-body-m': enableReskin,
                })}
              >
                {children}
              </p>
            ),
            hyperlink: ({children}) => (
              <UnderlinedTooltip
                id={product.product_name!.replace(/\s/g, '-').toLowerCase()}
                place="right"
                pageName="ProductSelectorSection"
                content={product.tooltip_content}
              >
                {children}
              </UnderlinedTooltip>
            ),
          }}
        />
      </div>
    </label>
  )
}
